import type { CSSProperties, MouseEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { B, bFonts } from "../tokens";
import { useBrand } from "../brand";

// "primary" and "tint" both use the brand color; "dark" is ink; "ghost" is a
// bordered neutral button.
type Kind = "primary" | "dark" | "ghost" | "tint";
type Size = "md" | "lg";

interface BBtnProps {
  children: ReactNode;
  kind?: Kind;
  size?: Size;
  to?: string;
  href?: string;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  type?: "button" | "submit";
  disabled?: boolean;
  full?: boolean;
  style?: CSSProperties;
}

const sizes: Record<Size, { padding: string; fontSize: number }> = {
  md: { padding: "9px 16px", fontSize: 14 },
  lg: { padding: "12px 22px", fontSize: 15 },
};

export default function BBtn({
  children,
  kind = "primary",
  size = "md",
  to,
  href,
  onClick,
  type = "button",
  disabled,
  full,
  style,
}: BBtnProps) {
  const { tint } = useBrand();
  const kinds: Record<Kind, CSSProperties> = {
    primary: { background: tint.ink, color: "#fff", boxShadow: "0 1px 2px rgba(15,23,42,.12)" },
    tint: { background: tint.ink, color: "#fff", boxShadow: "0 1px 2px rgba(15,23,42,.12)" },
    dark: { background: B.ink, color: "#fff", boxShadow: "0 1px 2px rgba(15,23,42,.18)" },
    ghost: { background: B.paper, color: B.ink, boxShadow: `inset 0 0 0 1px ${B.line}, 0 1px 2px rgba(15,23,42,.04)` },
  };

  const base: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    borderRadius: 8,
    fontWeight: 600,
    letterSpacing: "-0.005em",
    textDecoration: "none",
    cursor: disabled ? "not-allowed" : "pointer",
    fontFamily: bFonts.sans,
    border: 0,
    opacity: disabled ? 0.55 : 1,
    width: full ? "100%" : undefined,
    transition: "filter .12s, box-shadow .12s",
    ...sizes[size],
    ...kinds[kind],
    ...style,
  };

  if (href && !disabled) {
    return (
      <a href={href} onClick={onClick} style={base} className="b-btn">
        {children}
      </a>
    );
  }
  if (to && !disabled) {
    return (
      <Link to={to} onClick={onClick} style={base} className="b-btn">
        {children}
      </Link>
    );
  }
  return (
    <button onClick={onClick} type={type} disabled={disabled} style={base} className="b-btn">
      {children}
    </button>
  );
}
